/**
 * Custom SVG logo for the BLACKLINE mock squadron. Replaces the
 * Dicebear initials placeholder — reads as a real esports-clan mark
 * rather than auto-generated text.
 *
 * Design:
 *   - Hexagonal shield outline (military-patch geometry)
 *   - Dark-navy fill, primary-blue stroke
 *   - Bold "BL" monogram in white, centred
 *   - A literal black-line bar across the middle behind the letters
 *     (the squadron name made visual)
 *
 * Pure SVG — no rasterisation, scales crisply from icon-size (24px)
 * to hero-size (96px+). Component-based so we can tweak treatment in
 * one place if the mock squadron ever rebrands.
 */
export function BlacklineLogoMark({
  size = 64,
  className,
}: {
  size?: number
  className?: string
}) {
  return (
    <svg
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      className={className}
      role="img"
      aria-label="BLACKLINE squadron logo"
    >
      {/* Outer hex shield — flat-top variant, classic patch shape. */}
      <polygon
        points="32,3 57,17 57,47 32,61 7,47 7,17"
        fill="#06060f"
        stroke="#3D6FFF"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      {/* Inner hex outline — subtle, adds depth without competing
          with the lettering. */}
      <polygon
        points="32,9 51.5,20 51.5,44 32,55 12.5,44 12.5,20"
        fill="none"
        stroke="#3D6FFF"
        strokeWidth="1"
        strokeOpacity="0.3"
      />

      {/* The "black line" — full-width bar behind the lettering, the
          name made visual. Pure black with a thin primary-blue
          underline to keep it from looking like a missing-image bar. */}
      <rect x="7" y="30" width="50" height="6" fill="#000000" />
      <rect x="7" y="36" width="50" height="0.8" fill="#3D6FFF" />

      {/* "BL" monogram — sits on the black line. Heavy, tight kerning,
          slight italic-feel via skew for forward motion. */}
      <text
        x="32"
        y="40.5"
        textAnchor="middle"
        fontFamily='"Inter Tight", Inter, system-ui, sans-serif'
        fontWeight="900"
        fontSize="22"
        fill="#FFFFFF"
        letterSpacing="-1.5"
        style={{ fontStretch: 'condensed' }}
      >
        BL
      </text>

      {/* Top accent ticks — three short bars at the top edge, military
          chevron feel without committing to a full chevron shape. */}
      <rect x="22" y="13" width="4" height="1.5" fill="#3D6FFF" />
      <rect x="30" y="13" width="4" height="1.5" fill="#FFFFFF" opacity="0.6" />
      <rect x="38" y="13" width="4" height="1.5" fill="#3D6FFF" />
    </svg>
  )
}
